import { useMemo, useState } from 'react';
import { SortDirection } from 'src/components/Tables/SortCaret';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { useAllIfData } from 'src/hooks/query/ifData/useAllIfData';
import { cj } from 'src/utils/classNames';
import { WithClassName } from 'src/utils/UtilityTypes';
import { InsuranceFundOperationsMap } from '@velocity-exchange/common';

import TableHeader from './TableHeader';
import { TableBody } from './TableBody';
import { HeaderKey, InsuranceFundListData } from './types';
import { buildRowData, getSortedData } from './utils';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { MobileTableCards } from './MobileTableCards';

interface Props extends WithClassName {
	filter?: (data: InsuranceFundListData) => boolean;
	noTitle?: boolean;
	isPortfolioPage?: boolean;
}

export const InsuranceFundList = ({
	className,
	filter,
	noTitle = false,
	isPortfolioPage = false,
}: Props) => {
	const { data: ifStakeData } = useAllIfData();
	const getOraclePrice = useGetOraclePriceForMarket();

	const [sortState, setSortState] = useState<SortDirection>('neutral');
	const [currentSortHeader, setCurrentSortHeader] =
		useState<HeaderKey>('vaultBalance');
	const spotMarkets = useSpotMarkets();

	const rowData = spotMarkets.map((mkt) =>
		buildRowData(
			mkt.marketIndex,
			mkt.poolId,
			getOraclePrice,
			ifStakeData[mkt.marketIndex]
		)
	);
	const filteredRowData = rowData.filter(
		(_data, idx) =>
			!ifStakeData[spotMarkets[idx].marketIndex]?.pausedOperations?.includes(
				InsuranceFundOperationsMap.INIT
			) &&
			(filter?.(rowData[idx]) ?? true)
	);
	const isMobile = useIsMobileScreenSize();

	const sortedData = useMemo(
		() => getSortedData(filteredRowData, sortState, currentSortHeader),
		[filteredRowData, sortState, currentSortHeader]
	);

	const handleUpdateSort = (headerKey: HeaderKey) => {
		if (headerKey === currentSortHeader) {
			if (sortState === 'neutral') {
				setSortState('descending');
			} else if (sortState === 'descending') {
				setSortState('ascending');
			} else {
				setSortState('neutral');
			}
		} else {
			setCurrentSortHeader(headerKey);
			setSortState('descending');
		}
	};

	const style = useMemo(() => {
		if (!isPortfolioPage) {
			return {
				gridTemplateColumns: 'repeat(6, 1fr)',
			};
		}

		return { gridTemplateColumns: '1fr 1fr 1fr 120px' };
	}, [isPortfolioPage]);

	if (Object.keys(ifStakeData).length === 0) {
		return null;
	}

	return (
		<div className={cj('flex flex-col items-start gap-y-lg', className)}>
			{!noTitle && (
				<div className="hidden typo-label-md text-text-default sm:block">
					All Vaults
				</div>
			)}
			{!isMobile && (
				<div
					className="grid w-full overflow-hidden rounded-sm bg-container-bg"
					style={style}
				>
					<TableHeader
						handleUpdateSort={handleUpdateSort}
						currentSortHeader={currentSortHeader}
						sortState={sortState}
						isPortfolioPage={isPortfolioPage}
					/>
					<TableBody rowData={sortedData} isPortfolioPage={isPortfolioPage} />
				</div>
			)}
			{isMobile && (
				<MobileTableCards data={sortedData} isPortfolioPage={isPortfolioPage} />
			)}
		</div>
	);
};
